// app/frontend/pages/Changelog/Index.tsx
import { useState } from "react";
import {
  Box,
  Stack,
  Group,
  Paper,
  ActionIcon,
  Tooltip,
  Text,
} from "@mantine/core";
import { L2KBadge } from "../../components/ui/Typography";
import {
  IconRocket,
  IconMap,
  IconThumbUp,
  IconThumbUpFilled,
  IconSparkles,
  IconTrendingUp,
  IconBug,
  IconMovie,
  IconMapPin,
} from "@tabler/icons-react";
import {
  L2KH1,
  L2KH4,
  L2KH5,
  L2KText,
  L2KCaption,
} from "../../components/ui/Typography";
import { L2KTabs, L2KTabsPanel } from "../../components/ui/Tabs";
import HybridLayout from "../../components/layouts/HybridLayout";
import PageContainer from "../../components/layouts/PageContainer";
import { SEO } from "../../components/common/SEO";

interface ChangelogEntry {
  id: number;
  slug: string;
  title: string;
  description: string;
  category: string;
  category_label: string;
  category_color: string;
  status: string;
  status_label: string;
  status_color: string;
  is_roadmap: boolean;
  votes_count: number;
  published_at: string;
  published_at_short: string;
}

interface Props {
  updates: ChangelogEntry[];
  updates_by_month: Record<string, ChangelogEntry[]>;
  roadmap: ChangelogEntry[];
  roadmap_by_status: Record<string, ChangelogEntry[]>;
  user_votes: number[];
  is_authenticated: boolean;
}

export default function ChangelogIndex({
  updates,
  updates_by_month,
  roadmap,
  roadmap_by_status,
  user_votes: initialUserVotes,
  is_authenticated,
}: Props) {
  const [activeTab, setActiveTab] = useState<string | null>("updates");
  const [userVotes, setUserVotes] = useState<number[]>(initialUserVotes);
  const [voteCounts, setVoteCounts] = useState<Record<number, number>>(
    Object.fromEntries(
      [...updates, ...roadmap].map((e) => [e.id, e.votes_count])
    )
  );

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "features":
        return <IconSparkles size={16} />;
      case "improvements":
        return <IconTrendingUp size={16} />;
      case "bug_fixes":
        return <IconBug size={16} />;
      case "content":
        return <IconMovie size={16} />;
      case "roadmap":
        return <IconMapPin size={16} />;
      default:
        return <IconSparkles size={16} />;
    }
  };

  const handleVote = async (entry: ChangelogEntry) => {
    if (!is_authenticated) {
      window.location.href = "/users/sign_in";
      return;
    }

    try {
      const response = await fetch(`/changelog/${entry.slug}/vote`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setVoteCounts((prev) => ({ ...prev, [entry.id]: data.votes_count }));
        if (data.voted) {
          setUserVotes((prev) => [...prev, entry.id]);
        } else {
          setUserVotes((prev) => prev.filter((id) => id !== entry.id));
        }
      }
    } catch (error) {
      console.error("Failed to vote:", error);
    }
  };

  const hasVoted = (entryId: number) => userVotes.includes(entryId);

  const statusOrder = [
    "in_progress",
    "beta",
    "planned",
    "under_consideration",
    "complete",
    "declined",
  ];

  const statusLabels: Record<string, string> = {
    under_consideration: "💭 Under Consideration",
    planned: "📋 Planned",
    in_progress: "🔨 In Progress",
    beta: "🧪 Beta",
    complete: "✅ Complete",
    declined: "❌ Declined",
  };

  const tabs = [
    {
      value: "updates",
      label: "Updates",
      icon: <IconRocket size={18} />,
      count: updates.length,
    },
    {
      value: "roadmap",
      label: "Roadmap",
      icon: <IconMap size={18} />,
      count: roadmap.length,
    },
  ];

  return (
    <>
      <SEO
        title="Changelog & Roadmap - Learntokiz"
        description="See what's new and what's coming next to Learntokiz. Vote on features you want to see!"
      />
      <PageContainer>
        <Stack gap="xl" py="lg">
          {/* Header */}
          <Box ta="center">
            <L2KH1 variant="accent" mb="xs">
              Changelog & Roadmap
            </L2KH1>
            <L2KText variant="secondary" size="sm">
              See what's new and vote on what's coming next
            </L2KText>
          </Box>

          {/* Tabs */}
          <L2KTabs tabs={tabs} value={activeTab} onChange={setActiveTab}>
            {/* Updates Tab */}
            <L2KTabsPanel value="updates">
              <Stack gap="xl">
                {Object.keys(updates_by_month).length === 0 ? (
                  <Box ta="center" py="xl">
                    <L2KText variant="tertiary">
                      No updates yet. Check back soon!
                    </L2KText>
                  </Box>
                ) : (
                  Object.entries(updates_by_month).map(([month, entries]) => (
                    <Box key={month}>
                      <L2KH5 mb="md" c="#FF9800" size="sm">
                        {month}
                      </L2KH5>
                      <Stack gap="md">
                        {entries.map((entry) => (
                          <Paper
                            key={entry.id}
                            p="md"
                            radius="md"
                            style={{
                              backgroundColor: "rgba(255, 255, 255, 0.03)",
                              border: "1px solid rgba(255, 255, 255, 0.1)",
                            }}
                          >
                            <Group
                              justify="space-between"
                              align="flex-start"
                              mb="xs"
                            >
                              <Group gap="xs">
                                <L2KBadge
                                  color={entry.category_color}
                                  leftSection={getCategoryIcon(entry.category)}
                                  size="sm"
                                >
                                  {entry.category_label.replace(
                                    /^[^\s]+\s/,
                                    ""
                                  )}
                                </L2KBadge>
                                <L2KCaption variant="tertiary">
                                  {entry.published_at_short}
                                </L2KCaption>
                              </Group>
                            </Group>
                            <L2KH5 mb="xs">{entry.title}</L2KH5>
                            <L2KText
                              variant="secondary"
                              size="xs"
                              style={{ whiteSpace: "pre-wrap" }}
                            >
                              {entry.description}
                            </L2KText>
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  ))
                )}
              </Stack>
            </L2KTabsPanel>

            {/* Roadmap Tab */}
            <L2KTabsPanel value="roadmap">
              <Stack gap="xl">
                {!is_authenticated && (
                  <Paper
                    p="md"
                    radius="md"
                    style={{
                      backgroundColor: "rgba(255, 152, 0, 0.1)",
                      border: "1px solid rgba(255, 152, 0, 0.3)",
                    }}
                  >
                    <L2KText size="sm" ta="center">
                      <a href="/users/sign_in" style={{ color: "#FF9800" }}>
                        Sign in
                      </a>{" "}
                      to vote on features you want to see!
                    </L2KText>
                  </Paper>
                )}

                {roadmap.length === 0 ? (
                  <Box ta="center" py="xl">
                    <L2KText variant="tertiary">
                      No roadmap items yet. Check back soon!
                    </L2KText>
                  </Box>
                ) : (
                  statusOrder.map((status) => {
                    const entries = roadmap_by_status[status];
                    if (!entries || entries.length === 0) return null;

                    return (
                      <Box key={status}>
                        <L2KH4 mb="md">{statusLabels[status]}</L2KH4>
                        <Stack gap="md">
                          {entries.map((entry) => (
                            <Paper
                              key={entry.id}
                              p="md"
                              radius="md"
                              style={{
                                backgroundColor: "rgba(255, 255, 255, 0.03)",
                                border: "1px solid rgba(255, 255, 255, 0.1)",
                              }}
                            >
                              <Group justify="space-between" align="flex-start">
                                <Box style={{ flex: 1 }}>
                                  <Group gap="xs" mb="xs">
                                    <L2KBadge
                                      color={entry.category_color}
                                      leftSection={getCategoryIcon(
                                        entry.category
                                      )}
                                      size="sm"
                                    >
                                      {entry.category_label.replace(
                                        /^[^\s]+\s/,
                                        ""
                                      )}
                                    </L2KBadge>
                                  </Group>
                                  <L2KH5 mb="xs">{entry.title}</L2KH5>
                                  <L2KText
                                    variant="secondary"
                                    size="sm"
                                    style={{ whiteSpace: "pre-wrap" }}
                                  >
                                    {entry.description}
                                  </L2KText>
                                </Box>

                                {/* Vote Button */}
                                <Tooltip
                                  label={
                                    is_authenticated
                                      ? hasVoted(entry.id)
                                        ? "Remove vote"
                                        : "Vote for this"
                                      : "Sign in to vote"
                                  }
                                >
                                  <Box
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    <ActionIcon
                                      variant={
                                        hasVoted(entry.id) ? "filled" : "light"
                                      }
                                      color={
                                        hasVoted(entry.id) ? "orange" : "gray"
                                      }
                                      size="lg"
                                      onClick={() => handleVote(entry)}
                                    >
                                      {hasVoted(entry.id) ? (
                                        <IconThumbUpFilled size={20} />
                                      ) : (
                                        <IconThumbUp size={20} />
                                      )}
                                    </ActionIcon>
                                    <Text
                                      size="sm"
                                      fw={600}
                                      c={
                                        hasVoted(entry.id) ? "#FF9800" : "#888"
                                      }
                                    >
                                      {voteCounts[entry.id] || 0}
                                    </Text>
                                  </Box>
                                </Tooltip>
                              </Group>
                            </Paper>
                          ))}
                        </Stack>
                      </Box>
                    );
                  })
                )}
              </Stack>
            </L2KTabsPanel>
          </L2KTabs>
        </Stack>
      </PageContainer>
    </>
  );
}

// Use guest layout so anyone can view
ChangelogIndex.layout = (page: React.ReactNode) => (
  <HybridLayout>{page}</HybridLayout>
);
